import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Bell, Bot, Building2, HeartPulse, Key, LayoutDashboard, LogOut, ScrollText, ShieldCheck, UserCog, UserRound, Users } from 'lucide-react';
import { useAccess, useAuth } from '../context/AuthContext';
import { useApi } from '../lib/useApi';

const LEVEL_LABEL = { owner: 'Owner', admin: 'Admin', viewer: 'Read-only' } as const;

const NAV = [
  { title: '', items: [{ to: '/', label: 'Overview', icon: LayoutDashboard, end: true }] },
  { title: 'Customers', items: [
    { to: '/tenants', label: 'Tenants', icon: Building2 },
    { to: '/portal', label: 'Portal Signups', icon: UserRound },
    { to: '/notifications', label: 'Notifications', icon: Bell },
  ] },
  { title: 'Oversight', items: [
    { to: '/audit', label: 'Audit log', icon: ScrollText },
    { to: '/security', label: 'Security', icon: ShieldCheck },
    { to: '/assistant-security', label: 'FluxPilot security', icon: Bot },
    { to: '/health', label: 'System health', icon: HeartPulse },
    { to: '/platform-credentials', label: 'Platform credentials', icon: Key },
  ] },
  { title: 'Workspace', items: [
    { to: '/team', label: 'Team', icon: Users },
  ] },
];

export default function MainLayout() {
  const { user, logout } = useAuth();
  const { level } = useAccess();
  const navigate = useNavigate();
  // Polled every 30s regardless of which page is open, so a flagged jailbreak/injection attempt against
  // FluxPilot shows up here even if nobody currently has the FluxPilot security page open.
  const { data: securityCount } = useApi<{ unreviewed: number }>('/assistant-security-events/count', 30000);
  // Same urgency rule as PlatformCredentials.tsx's own status() -- not configured, expired, or
  // expiring within 14 days. Polled on an interval since the page itself isn't always open.
  const { data: credentials } = useApi<{ credentials: { configured: boolean; expires_at: string | null }[] }>('/platform-credentials', 60000);
  const urgentCredentials = (credentials?.credentials || []).filter(c => {
    if (!c.configured) return true;
    if (!c.expires_at) return false;
    const days = Math.round((new Date(c.expires_at + 'T00:00:00').getTime() - new Date(new Date().toDateString()).getTime()) / 86400000);
    return days <= 14;
  }).length;
  const badges: Record<string, number | undefined> = {
    '/assistant-security': securityCount?.unreviewed,
    '/platform-credentials': urgentCredentials || undefined,
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900">
      <aside className="fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r border-slate-200 bg-white">
        <div className="flex items-center gap-2.5 border-b border-slate-100 px-5 py-4">
          <img src="/logo.png" alt="FluxWatch" className="h-7 w-auto" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span className="rounded-md bg-slate-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">Admin</span>
        </div>
        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
          {NAV.map(group => (
            <div key={group.title || 'top'}>
              {group.title && <div className="mb-1.5 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{group.title}</div>}
              <div className="space-y-0.5">
                {group.items.map(item => {
                  const badge = badges[item.to];
                  return (
                    <NavLink key={item.to} to={item.to} end={(item as any).end}
                      className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors ${
                        isActive ? 'bg-blue-50 text-blue-800' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}>
                      {({ isActive }) => (<>
                        <item.icon className={`h-4 w-4 ${isActive ? 'text-blue-700' : 'text-slate-400'}`} />
                        <span className="flex-1">{item.label}</span>
                        {!!badge && (
                          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold text-white">
                            {badge > 99 ? '99+' : badge}
                          </span>
                        )}
                      </>)}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>
      <main className="ml-60 min-w-0 flex-1">
        <header className="sticky top-0 z-20 flex items-center justify-end gap-3 border-b border-slate-200 bg-white px-8 py-3">
          <NavLink to="/account" className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 ${isActive ? 'bg-blue-50' : 'hover:bg-slate-100'}`}>
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
              {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
            </span>
            <span className="min-w-0 text-left">
              <span className="block truncate text-sm font-medium text-slate-900">{user?.name || 'Super admin'}</span>
              <span className="block truncate text-xs text-slate-500">{LEVEL_LABEL[level]} · {user?.email}</span>
            </span>
            <UserCog className="h-4 w-4 shrink-0 text-slate-400" />
          </NavLink>
          <div className="h-8 w-px bg-slate-200" />
          <button onClick={async () => { await logout(); navigate('/login'); }}
            className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </header>
        <div className="w-full px-8 py-7"><Outlet /></div>
      </main>
    </div>
  );
}
